import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, FileText, AlertTriangle } from 'lucide-react';

interface Review {
  id: string;
  projectTitle: string;
  clientName: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  daysRemaining: number;
  submittedDate: string;
  targetCompletionDate: string;
  priority: 'low' | 'medium' | 'high';
}

interface AssignedReviewsListProps {
  reviews: Review[];
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'in_progress': return 'bg-blue-100 text-blue-800';
    case 'completed': return 'bg-green-100 text-green-800';
    case 'overdue': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getPriorityIcon = (priority: string) => {
  switch (priority) {
    case 'high': return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'medium': return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'low': return <Calendar className="h-4 w-4 text-green-500" />;
    default: return null;
  }
};

export const AssignedReviewsList = ({ reviews }: AssignedReviewsListProps) => {
  const navigate = useNavigate();

  const handleViewDetails = (review: Review) => {
    navigate(`/review/${review.id}`);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Assigned Reviews ({reviews.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {getPriorityIcon(review.priority)}
                    <h3 className="font-medium text-foreground">{review.projectTitle}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{review.clientName}</p>
                </div>
                <Badge className={getStatusColor(review.status)}>
                  {review.status.replace('_', ' ')}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mb-3">
                <div>
                  <span className="text-muted-foreground">Submitted:</span>
                  <p className="font-medium">{review.submittedDate}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Due:</span>
                  <p className="font-medium">{review.targetCompletionDate}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Days Remaining:</span>
                  <p className={`font-medium ${review.daysRemaining < 7 ? 'text-red-600' : review.daysRemaining < 14 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {review.daysRemaining}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleViewDetails(review)}>
                  View Details
                </Button>
                <Button variant="outline" size="sm">
                  Start Review
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};